import React from 'react';
import { Box, TableContainer, Table, Paper } from '@mui/material';
import { useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleDown } from '@fortawesome/free-solid-svg-icons';

import DashboardBody from '../../shared/dashboardBody';
import DashboardHeader from '../../shared/dashboardHeader';
import FilterControls from '../../shared/filterControls';
import Search from '../../shared/search';
import RenderIf from '../../renderIf';
import ShipyardMenuButton from '../../shared/shipyardMenu/ShipyardMenu';
import { MuiTablePagination } from '../../shared/muiControls';

import useRequestBuilder from './useRequestBuilder';
import BatchStatusUpdate from '../../shared/modals/batchStatusUpdate';
import useBatchStatus from '../../shared/modals/batchStatusUpdate/useBatchStatus';
import { ProtectedRouteContext } from '../../Context';
//#TODO These will be replaced in Sprint 13 when we have the database upgrade and/or more time for perfecting this.
import BatchPrint from '../batchPrint';
import useBatchPrint from '../batchPrint/useBatchPrint';

const InvoiceDashboard = ({ appTermId, location, stateRef, ...restOfProps }) => {
    const { userSettings } = React.useContext(ProtectedRouteContext);
    const { state: savedState } = useLocation();

    const [isCompressed, setIsCompressed] = React.useState(false);
    const [batchStatusOpen, setBatchStatusOpen] = React.useState(false);
    //#TODO These will be replaced in Sprint 13 when we have the database upgrade and/or more time for perfecting this.
    const [batchPrintOpen, setBatchPrintOpen] = React.useState(false);

    const tableRef = React.useRef();

    let {
        isFetching,
        page,
        rowsPerPage,
        headers,
        displayList: list,
        filters,
        searchValue,
        isInitialized,
        // hasMore, #TODO If the full dataset retrieval ever causes problems in terms of Lambda timeouts, this may need to come back.
        currentCount,
        totalRecordCount,
        totalUnfilteredCount,
        selectedFilters,
        selectedRows,
        handleFilterString,
        handleSort,
        handlePageChange,
        handleRowsPerPageChange,
        sortKey,
        sortDir,
        handleNav,
        retrieveList,
        // loadMore, #TODO If the full dataset retrieval ever causes problems in terms of Lambda timeouts, this may need to come back.
        handleRowSelection,
        refreshList,
    } = useRequestBuilder(savedState, stateRef);

    const { validateBatchUpdate } = useBatchStatus('invoices');

    //#TODO These will be replaced in Sprint 13 when we have the database upgrade and/or more time for perfecting this.
    const { validateBatchPrint } = useBatchPrint();

    React.useEffect(() => {
        retrieveList(appTermId, undefined, undefined, 0);
        if (tableRef.current && tableRef.current?.scrollTop !== 0) tableRef.current.scrollTop = 0;
    }, [appTermId]);

    const handleBatchStatusUpdateClick = async () => {
        let isValid = await validateBatchUpdate(selectedRows);
        if (isValid) setBatchStatusOpen(true);
    };

    const handleBatchStatusClose = (refresh) => {
        setBatchStatusOpen(false);
        if (refresh) refreshList(appTermId);
    };

    //#TODO These will be replaced in Sprint 13 when we have the database upgrade and/or more time for perfecting this.
    const handleBatchPrintClick = async () => {
        let isValid = await validateBatchPrint(selectedRows);
        if (isValid) setBatchPrintOpen(true);
    };

    return (
        <Box
            sx={{
                marginTop: (theme) => theme.spacing(1),
                paddingRight: (theme) => theme.spacing(3),
                paddingLeft: (theme) => theme.spacing(3),
            }}
        >
            <RenderIf isTrue={isInitialized && filters instanceof Object}>
                <FilterControls
                    isFetching={isFetching}
                    filters={filters}
                    currentCount={currentCount}
                    totalRecordCount={totalRecordCount}
                    totalUnfilteredCount={totalUnfilteredCount}
                    objectType="invoices"
                    handleFilterChange={(val, type) => handleFilterString(val, type, appTermId)}
                    selectedFilters={selectedFilters}
                />
            </RenderIf>

            <Paper
                sx={theme=> {
                    return {
                        marginTop: theme.spacing(2),
                        marginBottom: theme.spacing(2),
                        padding: theme.spacing(3),
                        borderRadius: '8px',
                        [theme.breakpoints.down('lg')]: {
                            maxWidth: '75vw',
                        },
                        [theme.breakpoints.down('md')]: {
                            maxWidth: '60vw',
                        },
                        [theme.breakpoints.down('sm')]: {
                            maxWidth: 'unset',
                        },
                    }
                }}
            >
                <RenderIf isTrue={isInitialized}>
                    <BatchStatusUpdate
                        open={batchStatusOpen}
                        closeCallback={handleBatchStatusClose}
                        selected={selectedRows}
                        ctx="invoices"
                    />
                    
                    {/*#TODO These will be replaced in Sprint 13 when we have the database upgrade and/or more time for perfecting this.*/}
                    <BatchPrint
                        open={batchPrintOpen}
                        selected={selectedRows}
                        headers={headers}
                        closeCallback={() => setBatchPrintOpen(false)}
                    />

                    <RenderIf isTrue={userSettings?.UserType >= 3}>
                        <Box
                            sx={{
                                display: 'flex',
                                justifyContent: 'right',
                            }}
                        >
                            <ShipyardMenuButton
                                label="Select Actions"
                                actions={[
                                    {
                                        label: 'Update selected invoices',
                                        action: handleBatchStatusUpdateClick,
                                    },                                    
                                    //#TODO These will be replaced in Sprint 13 when we have the database upgrade and/or more time for perfecting this.
                                    {
                                        label: 'Print selected invoices',
                                        action: handleBatchPrintClick,
                                    },
                                ]}
                                icon={<FontAwesomeIcon icon={faAngleDown} />}
                                disabled={isFetching}
                                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
                            />
                        </Box>
                    </RenderIf>

                    <Search
                        disabled={isFetching}
                        // searchText={searchHelperText}
                        searchValue={searchValue}
                        handleSearchChange={(val, type) => handleFilterString(val, type, appTermId)}
                        handleSwitch={() => setIsCompressed((prev) => !prev)}
                    />

                    <TableContainer
                        ref={tableRef}
                        sx={theme=> {
                            return {
                                maxHeight: '46.75vh',
                                overflow: 'auto',
                                [theme.breakpoints.down('sm')]: {
                                    maxHeight: '50vh',
                                },
                            };
                        }}
                    >
                        <Table
                            stickyHeader
                            size={isCompressed ? 'small' : 'medium'}
                        >
                            <RenderIf isTrue={headers}>
                                <DashboardHeader
                                    disabled={isFetching}
                                    headers={headers}
                                    handleSort={(key) => handleSort(key, appTermId)}
                                    handleCheckbox={(event) => handleRowSelection('all', event)}
                                    allSelected={list.length === selectedRows.length && list.length !== 0}
                                    sortKey={sortKey}
                                    sortDir={sortDir}
                                />
                            </RenderIf>

                            <RenderIf isTrue={headers && list}>
                                <DashboardBody
                                    list={list?.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)}
                                    headers={headers}
                                    isFetching={isFetching}
                                    handleNav={handleNav}
                                    selectedRows={selectedRows}
                                    handleCheckbox={(event, item) => handleRowSelection(item, event)}
                                />
                            </RenderIf>
                        </Table>
                    </TableContainer>                    

                    <MuiTablePagination
                        disabled={isFetching}
                        count={currentCount || 0}
                        totalCount={totalRecordCount || 0}
                        page={page}
                        onPageChange={handlePageChange}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleRowsPerPageChange}
                        showFirstButton={true}
                        showLastButton={true}
                    />
                </RenderIf>

                <RenderIf isTrue={!isInitialized}>Initializing invoices list...</RenderIf>
            </Paper>
        </Box>
    );
};

export default InvoiceDashboard;
